import { ReplyOrderRequest } from '../../api/punchlist/orders/types'
import {
  ORDER_ITEM_STATUS,
  ORDER_STATUS,
  ORDER_STATUS_REDUCED,
  ORDER_FILE_TYPE,
  ORDER_ITEM_IMAGE_TYPE,
  FEEDBACK_TYPE,
  ORDER_TYPE,
} from '../../helpers'

/** TYPES **/
export const ordersTypes = {
  FETCH_ORDERS: 'FETCH_ORDERS',
  FETCH_ORDER: 'FETCH_ORDER',
  FETCH_ORDER_ITEMS: 'FETCH_ORDER_ITEMS',
  FETCH_ORDER_FILES: 'FETCH_ORDER_FILES',
  FETCH_ORDER_NOTES: 'FETCH_ORDER_NOTES',
  ADD_ORDER_FILES: 'ADD_ORDER_FILES',
  DELETE_ORDER_FILE: 'DELETE_ORDER_FILE',
  UPDATE_ORDER_VALUES: 'UPDATE_ORDER_VALUES',
  SET_ORDERS: 'SET_ORDERS',
  ADD_ORDERS: 'ADD_ORDERS',
  SET_ACTIVE_ORDERS: 'SET_ACTIVE_ORDERS',
  SET_COMPLETED_ORDERS: 'SET_COMPLETED_ORDERS',
  REMOVE_ORDER: 'REMOVE_ORDER',
  SET_ORDER_VALUE: 'SET_ORDER_VALUE',
  SET_ORDER_VALUES: 'SET_ORDER_VALUES',
  UPDATE_ORDER_STATE: 'UPDATE_ORDER_STATE',
  UPDATE_ORDER_ITEM: 'UPDATE_ORDER_ITEM',
  UPDATE_ORDER_ITEM_IMAGES: 'UPDATE_ORDER_ITEM_IMAGES',
  SET_ORDER_ITEM_VALUES: 'SET_ORDER_ITEM_VALUES',
  SEND_ORDER_FEEDBACK: 'SEND_ORDER_FEEDBACK',
  REPLY_TO_ORDER: 'REPLY_TO_ORDER',
  ASSIGN_ORDER: 'ASSIGN_ORDER',
  UPDATE_JOB_SCHEDULE: 'UPDATE_JOB_SCHEDULE',
}

/** ACTIONS **/
export interface FetchOrdersPayload {
  status?: ORDER_STATUS_REDUCED
  assigneeId?: string
  assignerId?: string
  reset: boolean
}

export interface FetchOrdersAction {
  type: typeof ordersTypes.FETCH_ORDERS
  payload: FetchOrdersPayload
  callback?: (succ: boolean, count: number) => void
  [key: string]: any
}

export interface FetchOrderByIdAction {
  type: typeof ordersTypes.FETCH_ORDER
  payload: string
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface FetchOrderItemsAction {
  type: typeof ordersTypes.FETCH_ORDER_ITEMS
  payload: string
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface FetchOrderFilesAction {
  type: typeof ordersTypes.FETCH_ORDER_FILES
  payload: string
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface AddOrderFilesPayload {
  orderId: string
  files: Partial<OrderFile>[]
}

export interface AddOrderFilesAction {
  type: typeof ordersTypes.ADD_ORDER_FILES
  payload: AddOrderFilesPayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface DeleteOrderFilePayload {
  orderId: string
  fileId: string
}

export interface DeleteOrderFileAction {
  type: typeof ordersTypes.DELETE_ORDER_FILE
  payload: DeleteOrderFilePayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface FetchOrderNotesAction {
  type: typeof ordersTypes.FETCH_ORDER_NOTES
  payload: string
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface SetOrdersAction {
  type: typeof ordersTypes.SET_ORDERS
  payload: Order[]
  [key: string]: any
}

export interface AddOrdersAction {
  type: typeof ordersTypes.ADD_ORDERS
  payload: Order[]
  [key: string]: any
}

export interface SetActiveOrdersAction {
  type: typeof ordersTypes.SET_ACTIVE_ORDERS
  payload: Order[]
  [key: string]: any
}

export interface SetCompletedOrdersAction {
  type: typeof ordersTypes.SET_COMPLETED_ORDERS
  payload: Order[]
  [key: string]: any
}

export interface RemoveOrderAction {
  type: typeof ordersTypes.REMOVE_ORDER
  payload: string
  [key: string]: any
}

export interface OrderValue {
  orderId: string
  attr: keyof Order
  value: any
}

export interface SetOrderValueAction {
  type: typeof ordersTypes.SET_ORDER_VALUE
  payload: OrderValue
  [key: string]: any
}

export interface OrderValues {
  orderId: string
  values: Partial<Order>
}

export interface SetOrderValuesAction {
  type: typeof ordersTypes.SET_ORDER_VALUES
  payload: OrderValues
  [key: string]: any
}

export interface UpdateOrderValuesAction {
  type: typeof ordersTypes.UPDATE_ORDER_VALUES
  payload: OrderValues
  callback?: (succ: boolean) => void
  [key: string]: any
}

export type OrderState = 'none' | 'checkin' | 'complete' | 'submission'

export interface UpdateStatePayload {
  orderId: string
  orderState: OrderState
}

export interface UpdateOrderStateAction {
  type: typeof ordersTypes.UPDATE_ORDER_STATE
  payload: UpdateStatePayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface UpdateOrderItemPayload {
  orderId: string
  itemId: string
  partialItem: Partial<OrderItem>
}

export interface UpdateOrderItemAction {
  type: typeof ordersTypes.UPDATE_ORDER_ITEM
  payload: UpdateOrderItemPayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface UpdateOrderItemImagesPayload {
  orderId: string
  itemId: string
  list: Partial<ImageFile>[] | string[]
  opp: 'add' | 'remove' | 'replace'
}

export interface UpdateOrderItemImagesAction {
  type: typeof ordersTypes.UPDATE_ORDER_ITEM_IMAGES
  payload: UpdateOrderItemImagesPayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface OrderItemValues {
  orderId: string
  itemId: string
  attrs: Partial<OrderItem>
}

export interface SetOrderItemValuesAction {
  type: typeof ordersTypes.SET_ORDER_ITEM_VALUES
  payload: OrderItemValues
  [key: string]: any
}

export interface FeedbackPayload {
  orderId: string
  score: number
  callback?: () => void
}

export interface Feedback {
  userId: string
  resourceId: string
  resourceType: FEEDBACK_TYPE
  ratings: [
    {
      description?: string
      score: number
    }
  ]
  comments?: string
}

export interface SendOrderFeedbackAction {
  type: typeof ordersTypes.SEND_ORDER_FEEDBACK
  payload: FeedbackPayload
  [key: string]: any
}

export interface ReplyOrderPayload {
  orderId: string
  reply: ReplyOrderRequest
}

export interface ReplyToOrderAction {
  type: typeof ordersTypes.REPLY_TO_ORDER
  payload: ReplyOrderPayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface AssignOrderPayload {
  orderId: string
  vendorId: string
}

export interface AssignOrderAction {
  type: typeof ordersTypes.ASSIGN_ORDER
  payload: AssignOrderPayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export interface UpdateJobSchedulePayload {
  orderId: string
  schedulingStartDate: number
  schedulingEndDate: number
  expectedCompletedOn?: number
}

export interface UpdateJobScheduleAction {
  type: typeof ordersTypes.UPDATE_JOB_SCHEDULE
  payload: UpdateJobSchedulePayload
  callback?: (succ: boolean) => void
  [key: string]: any
}

export type OrdersActions =
  | FetchOrdersAction
  | FetchOrderItemsAction
  | FetchOrderFilesAction
  | FetchOrderNotesAction
  | AddOrderFilesAction
  | DeleteOrderFileAction
  | SetOrdersAction
  | SetActiveOrdersAction
  | SetCompletedOrdersAction
  | RemoveOrderAction
  | SetOrderValueAction
  | SetOrderValuesAction
  | UpdateOrderValuesAction
  | UpdateOrderStateAction
  | UpdateOrderItemAction
  | UpdateOrderItemImagesAction
  | SetOrderItemValuesAction
  | SendOrderFeedbackAction
  | ReplyToOrderAction
  | AssignOrderAction
  | UpdateJobScheduleAction

/** REDUCER **/
export interface OrderAddress {
  city: string
  state: string
  line1: string
  line2: string
  zipCode: string
  latitude: number
  longitude: number
}

export interface ImageFile {
  id: string
  name: string
  description: string
  fileType: ORDER_ITEM_IMAGE_TYPE
  fileUrl: string
  uploadedOn: Date
  uploadedBy: string
  notes: string
}

export interface OrderItem {
  orderId: string
  estimateId: string
  estimatePublicId: string
  itemId: string
  title: string
  inspectionReportReference: string
  quantity: number
  status: ORDER_ITEM_STATUS
  disclaimer: string
  location: string
  inspectionReportNote: string
  requestedRepairNote: string
  punchlistTechNote: string
  fieldNote: string
  imageFiles: ImageFile[]
  siteVisitId: string
  estimatedPrice: number
  totalPrice: number
  siteVisitNotes: string
  siteVisitComplete: boolean
  followupNotes: {
    note: string
    createdOn: number
    id: string
  }[]
}

export interface OrderFile {
  id: string
  name: string
  description: string
  fileType: ORDER_FILE_TYPE
  fileUrl: string
  uploadedOn: Date
  uploadedBy: string
}

export interface OrderNote {
  noteId: string
  orderId: string
  note: string
  createdBy: string
  createdOn: Date
}

export interface Incentive {
  name: string
  amount: number
  deadline: number
  notes: string
}

export interface Order {
  type: ORDER_TYPE
  orderId: string
  orderPublicId: string
  orderName: string
  statusType: ORDER_STATUS
  estimateId: string
  estimatePublicId: string
  startTimestamp: Date
  endTimestamp: Date
  bidPrice: number
  companyName: string
  companyContact: string
  address: OrderAddress
  scheduledStartTimestamp: number | null
  scheduledEndTimestamp: number | null
  checkedInTimestamp: number | null
  completedTimestamp: number | null
  vendorAcceptedTimestamp: number | null

  proTotalInvoiceAmount?: number | null

  showPriceToVendor: boolean

  statusTypeReduced: ORDER_STATUS_REDUCED

  incentives: Incentive[] | null
  incentiveTotalAmount: number | null
  incentiveStartDeadline: number | null

  items: OrderItem[]
  files: OrderFile[]
  notes: OrderNote[]

  assigneeId: string
  assigneeName: string
  checkedInBy: string
  assignedByName: string
  assignedByAddress: OrderAddress
  assignedByPicture: string
  preferredStartDate: number | null
  preferredCompletedOn: number | null

  homeownersName: string | null
  homeownersPhone: string | null
  homeownersEmail: string | null
  agentsName: string | null
  agentsPhone: string | null
  agentsEmail: string | null
  schedulersName: string | null
  schedulersPhone: string | null
  schedulersEmail: string | null
  accessInfo: string | null
  lockboxDetails: string | null
  closingDate: number | null
  occupancy: boolean | null
  childrenUnder18: boolean | null
  pets: boolean | null
}

export interface OrdersType extends Array<Order> { }
